import { useId, type ReactNode } from 'react'

import type { PreflightBlocker, PreflightResult, PreflightWarning } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { EmptyState } from '../../atoms/EmptyState.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { errorMessageOf, moduleLabelOf, orDash } from '../format.ts'
import { GroupedPreflightReport, GroupedWarningList } from './GroupedPreflightReport.tsx'

import '../screens.css'

/**
 * 사전 점검 결과 — `POST /batches/{id}/preflight` 응답을 **그대로** 그린다.
 *
 * 🔴 **와이어프레임의 9항목 고정 격자(`PF_ITEMS`)를 대신한다**(2026-09-20 · 팀장 결정).
 *    계약 `PreflightResult` 는 **`passed` + `blockers` 둘뿐**이고 항목 목록은 어느 응답에도
 *    없었다 — 격자를 그리면 화면이 「9가지를 점검했다」고 말하는 셈인데 서버는 그런 말을
 *    한 적이 없다. 되살리지 마라.
 *
 * 🔴 **`inconclusive` 를 `fail` 로 접지 않는다.** 계약이 제3값으로 가른 이유가 있다 —
 *    「막혔다」와 「못 쟀다」는 **사람이 할 일이 다르다**. 접으면 맞게 놓은 것을 계속 다시 놓는다.
 *    ⚠ `pass` 는 이 목록에 오지 않는다(계약 문면). 통과는 `passed` 가 말한다.
 *
 * 🔴 **`passed: true` 인데 `blockers` 가 차 있다**(2026-09-20 실물 배치에서 확인 —
 *    `VISION_INSPECT_INCONCLUSIVE_AT_START` · `REAGENT_TEMP_NOT_REACHED` 2건, 둘 다
 *    `inconclusive`). **필드 이름이 `blockers` 라고 전부 막은 것이 아니다** — 막은 것은
 *    `verdict: 'fail'` 뿐이고 나머지는 「못 쟀다」다. 그래서 목록의 **이름을 판정으로 정한다**:
 *    `fail` 이 하나라도 있으면 「차단 사유」, 전부 미판정이면 「미판정 항목」.
 *    ⚠ 서버가 준 배열을 **거르지 않는다.** 이름만 바꾼다 — 못 쟀다는 사실도 사람이 봐야 한다.
 *
 * ⚠ 문구는 **에러 코드 사전을 경유**하고, 사전에 없는 코드는 **원문 그대로** 둔다 —
 *   화면이 문장을 지어내면 카탈로그와 두 벌이 되어 반드시 어긋난다. `action_required` 도
 *   서버가 카탈로그 `suggested_actions` 를 **인용**한 것이라 손대지 않는다.
 *
 * 🔴 **경고(`warnings`)는 차단과 «다른 목록»이다**(2026-09-22 신설). 막지 않는 대신
 *    **시작에 확약을 요구**한다(`StartBatchEnvelope.warn_ack`). 한 목록에 접으면 사람이
 *    「시작 버튼이 꺼졌다」로 읽고, 반대로 안 보이면 **무엇을 확약하는지 모른 채 누른다.**
 *    ⚠ `verdict` 가 없다 — 판정 배지를 달지 않는다. 대신 서버가 잰 값(`detail`)을 보인다.
 *    ⚠ **`warnings` 가 «없는 것»과 «빈 것»을 가른다** — 없으면 줄 자체를 그리지 않는다
 *      (그 판이 경고를 안 싣는 것이고, 「경고 0건」이라는 판정이 아니다).
 *
 * 경계 다섯 중 이 자리에 오는 넷: 로딩(`running`) · 에러(`error`) · 빈(차단 0건) ·
 * 미실행(`startedOnce === false`). 권한·끊김은 실행 버튼의 `gate()` 가 진다.
 */
/**
 * 줄 하나에 덧붙일 것 — 줄 머리 오른쪽의 조치 단추(`actions`)와 줄 아래 진행 · 결과 한 줄(`after`).
 * 🔴 무엇을 누를지는 부르는 쪽이 정한다(작업 줄 시작 창 · `shell/StartPreflightActions.tsx`). 이 보고서는 서버 결과를 그리는 자리라 훅을 모른다.
 */
export interface PreflightRowExtra {
  actions?: ReactNode
  after?: ReactNode
}
export type PreflightRowExtraFn = (
  row: PreflightBlocker | PreflightWarning,
) => PreflightRowExtra | undefined

export interface PreflightReportProps {
  presentation?: 'list' | 'grouped' | undefined
  /** 대화 제목과 겹치는 카드 제목만 생략한다. 표 접근 이름은 유지한다. */
  hideTitle?: boolean | undefined
  result?: PreflightResult | undefined
  running?: boolean
  error?: string | undefined
  /** 작업이 만들어져 점검할 대상이 생겼는가 */
  startedOnce?: boolean
  /** 줄마다 조치 단추 · 결과 한 줄(시작 창 · 2026-10-23). 안 주면 종전 그대로다 */
  rowExtra?: PreflightRowExtraFn | undefined
}

export function PreflightReport({
  result,
  running = false,
  error,
  startedOnce = false,
  rowExtra,
  presentation = 'list',
  hideTitle = false,
}: PreflightReportProps) {
  /* 훅은 조건보다 먼저다 — 아래 이른 반환들이 있어서 여기서 받는다 */
  const listLabelId = useId()

  if (error !== undefined) {
    return (
      <p className="deny" role="alert">
        {error}
      </p>
    )
  }
  if (running) return <EmptyState label={t('state.loading')} />
  if (result === undefined) {
    return (
      <EmptyState
        label={startedOnce ? t('state.empty.preflightNotRun') : t('reason.afterBatchCreated')}
      />
    )
  }

  /*
   * 🔴 **목록의 이름은 판정이 정한다.** `passed` 가 아니다 — 막힌 회차에도 미판정이 섞일 수
   *    있고, 통과한 회차에도 미판정만 남을 수 있다. 둘을 가르는 축은 `verdict: 'fail'` 하나다.
   */
  if (presentation === 'grouped')
    return <GroupedPreflightReport result={result} rowExtra={rowExtra} hideTitle={hideTitle} />

  const blocked = result.blockers.some((blocker) => blocker.verdict === 'fail')
  const listLabel = blocked ? t('preflight.blockers') : t('preflight.unresolved')

  return (
    <div className="pfres">
      <div className="pfres__head">
        <StatusBadge
          label={result.passed ? t('preflight.passed') : t('preflight.blocked')}
          status={result.passed ? 'completed' : 'error'}
        />
      </div>
      {result.blockers.length === 0 ? (
        <EmptyState label={t('state.empty.blockers')} />
      ) : (
        <>
          {/* 🔴 **눈에 보이는 이름이 있어야 한다.** `aria-label` 만 두면 보는 사람은 「통과」
              배지 아래 목록이 무엇인지 모른 채 차단으로 읽는다 — 실제로 그렇게 읽혔다 */}
          <div className="pfres__head" id={listLabelId}>
            {listLabel}
          </div>
          <ul aria-labelledby={listLabelId} className="pfblk">
            {result.blockers.map((blocker, index) => (
              <BlockerRow
                blocker={blocker}
                extra={rowExtra?.(blocker)}
                key={`${blocker.code}-${String(index)}`}
              />
            ))}
          </ul>
        </>
      )}
      <WarningList rowExtra={rowExtra} warnings={result.warnings} />
    </div>
  )
}

/**
 * 경고 목록. 🔴 **`undefined` 면 아무것도 그리지 않는다** — 「이 응답이 경고를 안 싣는다」이지
 * 「경고가 없다」가 아니다. 빈 배열은 서버가 «재고 확인해 0건»이라 말한 것이라 그렇게 적는다.
 *
 * 🔴 **내보내는 까닭** — 시작 버튼의 확약 대화(`shell/BatchStripContainer.tsx`)가 **같은 목록**을
 *    그린다. 두 벌로 만들면 사람이 점검 화면에서 본 것과 확약할 때 보는 것이 갈리고,
 *    **그 둘이 갈리면 서버가 `warn_ack_mismatch` 로 거절한다.** 얼굴을 하나로 둔다.
 */
export function WarningList({
  warnings,
  rowExtra,
  presentation = 'list',
  hideTitle = false,
}: {
  warnings?: PreflightWarning[] | undefined
  rowExtra?: PreflightRowExtraFn | undefined
  presentation?: PreflightReportProps['presentation']
  hideTitle?: boolean | undefined
}) {
  const labelId = useId()
  if (presentation === 'grouped')
    return <GroupedWarningList warnings={warnings} rowExtra={rowExtra} hideTitle={hideTitle} />
  if (warnings === undefined) return null
  return (
    <>
      <div className="pfres__head" id={labelId}>
        {t('preflight.warnings')}
      </div>
      {warnings.length === 0 ? (
        <EmptyState label={t('state.empty.warnings')} />
      ) : (
        <ul aria-labelledby={labelId} className="pfblk">
          {warnings.map((warning, index) => (
            <WarningRow
              extra={rowExtra?.(warning)}
              key={`${warning.code}-${String(index)}`}
              warning={warning}
            />
          ))}
        </ul>
      )}
    </>
  )
}

function WarningRow({
  warning,
  extra,
}: {
  warning: PreflightWarning
  extra?: PreflightRowExtra | undefined
}) {
  const target = [
    warning.module_id === null ? undefined : moduleLabelOf(warning.module_id),
    warning.item === null ? undefined : warning.item,
  ]
    .filter((value): value is string => value !== undefined)
    .join(' · ')

  return (
    <li className="pfblk__item">
      <div className="pfblk__head">
        {/* 🔴 판정 배지가 «없다» — 경고에는 `verdict` 가 서지 않는다(계약 문면) */}
        <StatusBadge label={t('enum.severity.WARN')} status="WARN" />
        <span className="pfblk__title">{errorMessageOf(warning.code, warning.code)}</span>
        {extra?.actions}
      </div>
      {target === '' ? null : <div className="pfblk__target">{target}</div>}
      {/* 🔴 서버가 «이 배치에서 잰 수»를 적은 칸이다 — 카탈로그 문면이 아니다(소요·선언·여유) */}
      {warning.detail === null ? null : <div className="pfblk__target">{warning.detail}</div>}
      {warning.action_required === null ? null : (
        <div className="pfblk__action">{orDash(warning.action_required)}</div>
      )}
      {extra?.after}
    </li>
  )
}

function BlockerRow({
  blocker,
  extra,
}: {
  blocker: PreflightBlocker
  extra?: PreflightRowExtra | undefined
}) {
  /**
   * 🔴 대상은 **모듈과 항목이 다른 축이다.** 합쳐 한 문자열로 만들면 어느 쪽이 빈 것인지
   *    알 수 없다 — 있는 것만 이어 붙이고 둘 다 없으면 줄 자체를 그리지 않는다.
   */
  const target = [
    blocker.module_id === null ? undefined : moduleLabelOf(blocker.module_id),
    blocker.item === null ? undefined : blocker.item,
  ]
    .filter((value): value is string => value !== undefined)
    .join(' · ')

  return (
    <li className="pfblk__item">
      <div className="pfblk__head">
        <StatusBadge
          label={enumLabel('enum.verdict', blocker.verdict)}
          status={blocker.verdict === 'fail' ? 'error' : 'inconclusive'}
        />
        {/* 사전에 있으면 사전 문구, 없으면 **서버가 준 코드 원문**. 문장을 만들지 않는다 */}
        <span className="pfblk__title">{errorMessageOf(blocker.code, blocker.code)}</span>
        {extra?.actions}
      </div>
      {target === '' ? null : <div className="pfblk__target">{target}</div>}
      {blocker.action_required === null ? null : (
        <div className="pfblk__action">{orDash(blocker.action_required)}</div>
      )}
      {extra?.after}
    </li>
  )
}
